import { forwardRef, useEffect, useRef, useState } from 'react';
import FormHelperText from '@mui/material/FormHelperText';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';

const getLabelbyValue = (array, value) => {
  const item = array.find((object) => object.value === value);
  if (item === undefined) {
    return value;
  }
  return item.label;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

/**
 * Select based on MUI Select.
 * #### Require external useState() controller
 *
 * @component
 * @param {Object} props - Generic object of functional component
 * @param {boolean} props.error - if has been ocurred an error during selecting option, set flag to true
 * @param {string} props.helperText - Error text to show.
 * @param {title} props.title - Select label to show
 * @param {array} props.data - Data to show as options, is Array of Object [{label, value},...]
 * @param {array} props.value - Set the initial value or controled value from state, is Array of strings | number
 * @param {string} props.clear - Clear the array of chips selected by the user
 * @param {function} props.onChange - Event to listen state change to current value selected.
 * @returns {JSX.Element} Returns JSX Elements render of MUI Select.
 *
 * @example
 * // Render a select with data
 * const options = [
 *  {
 *    label: 'Opcion 1'
 *    value: 'opt_1'
 *  },
 * {
 *    label: 'Opcion 1'
 *    value: 'opt_2'
 *  },
 * ]
 * <BasicSelect title="Simple select" data={options} />
 */
export const ChipSelect = forwardRef(function ChipSelect(
  {
    error = false,
    helperText,
    title,
    clear = false,
    data = [],
    value = [],
    onChange = () => {},
    ...props
  },
  ref
) {
  const [selected, setSelected] = useState(value);
  const [values] = useState(data);
  const clearRef = useRef(null);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setSelected(selectedValue);
  };

  useEffect(() => {
    onChange(selected);
  }, [selected]);

  useEffect(() => {
    clearRef.current = clear;
    if (!clearRef.current) {
      return;
    }
    setSelected([]);
    clearRef.current = false;
  }, [clear]);

  return (
    <div className="flex flex-row flex-wrap justify-center items-center w-full">
      <FormControl fullWidth error={error}>
        <InputLabel id="chip-multiple-label">{title}</InputLabel>
        <Select
          ref={ref}
          {...props}
          labelId="chip-multiple-label"
          id="chip-multiple-label"
          multiple
          fullWidth
          value={selected || []}
          onChange={handleChange}
          input={<OutlinedInput id="select-multiple-chip" label={title} />}
          MenuProps={MenuProps}
          renderValue={(items) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {items.map((current) => (
                <Chip key={`${current}`} label={getLabelbyValue(values, current)} />
              ))}
            </Box>
          )}
        >
          {values.map((current, index) => (
            <MenuItem key={`${current.label}_${index}_item`} value={current.value}>
              {current.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    </div>
  );
})

ChipSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  title: PropTypes.string.isRequired,
  clear: PropTypes.bool,
  onChange: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
};
